import React from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Link } from "react-router-dom";

const HowItWorks = () => {
  const steps = [
    {
      number: "1",
      title: "Choose Your Program",
      description: "Select from our DevOps & Cloud Automation, Full-Stack MERN Development, or Combo Package based on your career goals.",
      icon: "🎯",
      color: "#007bff"
    },
    {
      number: "2",
      title: "Enroll & Get Started",
      description: "Complete the registration process and make your payment. You'll receive immediate access to course materials and resources.",
      icon: "📝",
      color: "#28a745"
    },
    {
      number: "3",
      title: "Learn & Practice",
      description: "Complete assignments, work on projects, and participate in daily QA sessions to build your skills.",
      icon: "📚",
      color: "#ffc107"
    },
    {
      number: "4",
      title: "Build Your Portfolio",
      description: "Create real-world projects that showcase your skills. Build a professional portfolio that impresses employers.",
      icon: "💼",
      color: "#17a2b8"
    },
    {
      number: "5",
      title: "Get Job Assistance",
      description: "Receive help with resume building, mock interviews, and career guidance. We'll support you until you land your dream job.",
      icon: "🚀",
      color: "#dc3545"
    },
    {
      number: "6",
      title: "Launch Your Career",
      description: "With your new skills, portfolio, and our support, you're ready to start your career in technology. Congratulations!",
      icon: "🎉",
      color: "#6f42c1"
    }
  ];

  const features = [
    {
      title: "Recorded Videos",
      description: "Access to all recorded sessions for lifetime",
      icon: "🎥"
    },
    {
      title: "Hands-On Projects",
      description: "Real-world projects to build your portfolio",
      icon: "🛠️"
    },
    {
      title: "Daily Support",
      description: "Daily QA sessions and doubt clearing",
      icon: "💬"
    },
    {
      title: "Mentorship",
      description: "Personalized guidance from industry experts",
      icon: "👨‍🏫"
    },
    {
      title: "Job Placement",
      description: "Comprehensive job assistance program",
      icon: "💼"
    }
  ];

  return (
    <Container className="page-shell how-page my-5">
      <Row className="mb-5 page-header text-center">
        <Col>
          <span className="eyebrow dark">Process</span>
          <h1 className="mb-3 fw-bold">How It Works</h1>
          <p className="page-subtitle mx-auto">
            Our streamlined process makes it easy to start your journey from enrollment to landing your dream job.
            Here's how we help you succeed.
          </p>
        </Col>
      </Row>

      <Row className="mb-5">
        <Col>
          <div className="position-relative how-process-wrap">
            <div className="d-none d-md-block how-process-line" />

            <Row className="g-4">
              {steps.map((step, index) => (
                <Col
                  key={index}
                  xs={12}
                  md={6}
                  className={index % 2 === 0 ? "pe-md-5" : "ps-md-5"}
                >
                  <Card className="h-100 step-card border-0 position-relative how-step-card" style={{ zIndex: 1 }}>
                    <Card.Body className="p-4">
                      <div className="d-flex align-items-start mb-3">
                        <div
                          className="step-number-badge d-flex align-items-center justify-content-center text-white fw-bold me-3"
                          style={{ backgroundColor: step.color }}
                        >
                          {step.number}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center mb-2 step-header">
                            <span className="step-icon me-2">{step.icon}</span>
                            <Card.Title className="mb-0">{step.title}</Card.Title>
                          </div>
                          <Card.Text className="step-description mb-0">{step.description}</Card.Text>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </Col>
      </Row>

      <Row className="mb-5">
        <Col>
          <h2 className="text-center mb-4">What You Get</h2>
          <Row className="g-4 justify-content-center">
            {features.map((feature, index) => (
              <Col key={index} xs={12} sm={6} md={4}>
                <Card className="h-100 info-card border-0 text-center how-feature-card">
                  <Card.Body className="p-4">
                    <div className="feature-icon mb-3">{feature.icon}</div>
                    <Card.Title>{feature.title}</Card.Title>
                    <Card.Text className="text-muted">{feature.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      <Row className="mb-5">
        <Col>
          <h2 className="text-center mb-4">Course Timeline</h2>
          <Row>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 how-timeline-card">
                <Card.Body className="p-4 text-center">
                  <h3 className="mb-3">Weeks 1-4</h3>
                  <h5>Foundation</h5>
                  <p className="mb-0">Build strong fundamentals with core concepts and basic tools.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 how-timeline-card">
                <Card.Body className="p-4 text-center">
                  <h3 className="mb-3">Weeks 5-8</h3>
                  <h5>Intermediate</h5>
                  <p className="mb-0">Dive deeper into advanced topics and start building projects.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 how-timeline-card">
                <Card.Body className="p-4 text-center">
                  <h3 className="mb-3">Weeks 9-12+</h3>
                  <h5>Advanced & Placement</h5>
                  <p className="mb-0">Master advanced concepts, complete portfolio, and job preparation.</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="border-0 cta-panel text-white how-cta-panel">
            <Card.Body className="p-5 text-center">
              <h3 className="mb-3">Ready to Start Your Journey?</h3>
              <p className="lead mb-4 text-white-50">
                Join hundreds of students who have successfully launched their careers with SPARKLOGIC.
                Your future in technology starts here.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/pricing" className="btn btn-light btn-lg">View Pricing</Link>
                <Link to="/contact" className="btn btn-outline-light btn-lg">Contact Us</Link>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default HowItWorks;

